import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { Icons, LanguageUtils, WellnessPlans } from '@pbt/pbt-ui-components'
import { Wellness as WellnessIcon } from '@pbt/pbt-ui-components/src/icons'

import CheckboxListFilter, {
  type CheckboxListFilterProps,
} from '~/components/common/filters/CheckboxListFilter'
import { MembershipFilters } from '~/constants/wellnessPlansConstants'
import { fetchClientsMembershipFilters } from '~/store/actions/clients'
import {
  getClientsIsFetchingFilters,
  getClientsMembershipFilters,
} from '~/store/reducers/clients'
import type { Filter } from '~/types'
import { getUrlSearchParam } from '~/utils'

const useStyles = makeStyles(
  (theme) => ({
    icon: {
      verticalAlign: 'middle',
      marginRight: theme.spacing(1),
    },
  }),
  { name: 'MembershipFilter' },
)

const getIsCancelled = (filter: Filter) =>
  filter.param === MembershipFilters.WELLNESS_PLAN_CANCELLED
const getIsPaused = (filter: Filter) =>
  filter.param === MembershipFilters.WELLNESS_PLAN_STATUS &&
  filter.value === WellnessPlans.MembershipStatus.PAUSED
const getHasNoMembership = (filter: Filter) =>
  filter.param === MembershipFilters.MEMBERSHIP_STATUS && filter.value === false

export interface MembershipFilterProps
  extends Omit<CheckboxListFilterProps, 'value'> {}

const MembershipFilter = ({ open, ...rest }: MembershipFilterProps) => {
  const classes = useStyles()
  const location = useLocation()
  const dispatch = useDispatch()
  const membershipFilters: Filter[] = useSelector(getClientsMembershipFilters)
  const isFetching = useSelector(getClientsIsFetchingFilters)

  const fieldsQuery = getUrlSearchParam('fieldsQuery', location.search)
  const value = fieldsQuery?.split(';')

  const hasAllCancelledIconVariant = Icons.IconVariant.SUSPENSED

  const hasPausedIconVariant = Icons.IconVariant.SUSPENSED

  const hasNoMembershipIconVariant = Icons.IconVariant.INACTIVE

  const getIconVariantByFilter = R.cond([
    [getIsCancelled, R.always(hasAllCancelledIconVariant)],
    [getIsPaused, R.always(hasPausedIconVariant)],
    [getHasNoMembership, R.always(hasNoMembershipIconVariant)],
    [R.T, R.always(Icons.IconVariant.PRIMARY)],
  ])

  useEffect(() => {
    if (open) {
      dispatch(fetchClientsMembershipFilters())
    }
  }, [open])

  const items = membershipFilters.map((filter) => {
    const variant = getIconVariantByFilter(filter)

    return {
      ...filter,
      id: `${filter.param}=${filter.value}`,
      component: (
        <>
          <WellnessIcon className={classes.icon} variant={variant} />
          {LanguageUtils.getTranslatedFieldName(filter)}
        </>
      ),
    }
  })

  return (
    <CheckboxListFilter
      arrayFormat
      includeSearch={false}
      isLoading={isFetching}
      items={items}
      open={open}
      value={value}
      {...rest}
    />
  )
}

export default MembershipFilter
